import React from 'react';
import { AppChrome } from '../alt/AppChrome';
import { WorkflowsScreen, PulsesScreen } from '../alt/PlatformPillar';
import { ConnectionsScreen } from '../alt/ConnectedAppsPillar';
import { SlackPanel } from '../SlackIntegrationGraph';
import { SkillShelf } from './SkillShelf';
import { AgentBuilderCard, ApprovalCard, ConnectionsCard } from '../aiagents/agentArtefacts';
import { TrackWorkspace } from '../gigachat/TrackWorkspace';
import { GigaChatScreen } from './GigaChatScreen';
import type { VerticalCrop } from './CreatorFrame';

/**
 * THE CREATOR PACK — the same product, composed for vertical video.
 *
 * A short, opinionated set rather than another full library. Someone cutting a
 * 30-second clip does not need twenty frames; they need the handful that carry
 * a story: what Giga is plugged into, it answering where the team already
 * talks, it answering in its own app, work running on a schedule, and the
 * library of methods behind all of it.
 *
 * Ordered as that story rather than by product area, so the pack cuts in
 * sequence straight down the page.
 *
 * Every plate is a STILL. Nothing here is interactive and nothing animates in
 * — an asset that has to be scrolled into view or clicked to look right is an
 * asset that exports wrong.
 */
export type CreatorAsset = {
  id: string;
  name: string;
  note: string;
  use: string;
  /** Natural width of the screen inside, for the plate's scale maths. */
  width: number;
  /**
   * Width the screen is rendered at ON the plate — the same in both ratios, so
   * switching shape changes the surrounding space and never the zoom. Only set
   * this when a screen needs to run wider than the default 508px.
   */
  renderWidth?: number;
  /**
   * How the 9:16 plate crops in. Vertical video is watched on a phone, so a
   * whole desktop screen scaled to a 486px column is unreadable — these plates
   * show a legible part instead of an illegible whole. `zoom` is the scale the
   * screen renders at inside the crop (≈1 means true size); `x` trims natural
   * px off the left when the point sits off-centre.
   */
  crop?: VerticalCrop;
  /**
   * A SINGLE OBJECT rather than a full app screen — a prompt, a settings card.
   *
   * These two facts always travel together, which is why they are one flag:
   * an object is rendered smaller than a screen (a 230px card blown up to fill
   * the artboard reads as a zoomed screenshot, not as a card sitting in a
   * frame), and it is centred vertically rather than ridden high, because it
   * does not fill the frame and the reserved caption band below a screen
   * becomes a void below an object.
   */
  object?: boolean;
  /**
   * Centre the plate vertically without the size reduction `object` brings.
   * For assets that already fill their own frame — a bled grid — but should
   * not ride high behind a caption band.
   */
  centered?: boolean;
  /** Optional narrower composition used only inside the 9:16 crop. */
  renderVertical?: () => React.ReactNode;
  render: () => React.ReactNode;
};
export const CREATOR_ASSETS: CreatorAsset[] = [{
  id: 'creator-connections',
  name: 'Integrations',
  note: 'Everything Giga is plugged into',
  use: 'The opening shot for "it works with what you already use". The densest screen in the pack — hold it a beat longer than the others, and prefer 16:9 where its own layout has room.',
  width: 1240,
  // Widest natural screen here, so it runs to the artboard's edges rather than
  // the shared default — below this its account labels stop reading.
  renderWidth: 512,
  // The connections table's meaning lives in its left half — the tool, its
  // name, its status. The right-hand columns are the first thing that becomes
  // unreadable on a phone anyway, so vertical drops them and keeps the rows at
  // two-thirds true size instead of showing all of it at a third.
  crop: {
    zoom: 0.66,
    x: 8
  },
  render: () => <AppChrome sidebar={false} data-id="element-3275" data-mp="3hbvwmwup8qs0">
      <ConnectionsScreen data-id="element-3276" data-mp="3hcxcdemnu1g2" />
    </AppChrome>
}, {
  id: 'creator-slack',
  name: 'Slack thread',
  note: 'Someone asks in #sales, Giga answers',
  use: 'The most legible plate in the pack — a message column reads well narrow. Use it for the "no new app to learn" beat.',
  width: 900,
  // A thread is a column of text, so vertical is its natural shape: near true
  // size, cropped to the message column.
  crop: {
    zoom: 0.92
  },
  render: () => <SlackPanel className="w-full" heightClass="h-[560px]" platform="slack" showPlatformToggle={false} settled data-id="element-3277" data-mp="3020dhq15qvs6" />
}, {
  id: 'creator-chat',
  name: 'Giga Chat',
  note: 'A sourced pipeline answer in the Brightwave workspace',
  use: 'The product itself. Use it to show Giga reading company context, completing the routine path, and clearly surfacing exceptions.',
  width: 1100,
  renderWidth: 510,
  crop: {
    zoom: 0.8,
    x: 220
  },
  render: () => <GigaChatScreen data-id="element-3278" data-mp="2hb0mkja3rbw9" />
}, {
  id: 'creator-workflows',
  name: 'Workflows',
  note: 'Steps, and the record behind them',
  use: 'For the "it keeps working" beat. The strip beside the name is the part worth pausing on — 26 runs at 96%, its owner, and who can run it. That is the evidence; the steps are just the detail.',
  width: 900,
  // Padded the same way the landscape frames pad it — these two screens do
  // not carry their own inner gutter.
  crop: {
    zoom: 0.88
  },
  render: () => <AppChrome sidebar={false} data-id="element-3279" data-mp="pey7ur3bm6va">
      <div className="px-7 py-6" data-id="element-3280" data-mp="phuautu0zlql">
        <WorkflowsScreen data-id="element-3281" data-mp="kgmy0gd0zwd2" />
      </div>
    </AppChrome>
}, {
  id: 'creator-pulses',
  name: 'Pulses',
  note: 'One running, three scheduled',
  use: 'The shortest plate to read in the pack — four rows, one of them mid-run with its step bar and a live Running chip, and a fired count on each. Good for a fast cut, or as the closing card under a voiceover.',
  width: 900,
  crop: {
    zoom: 0.88
  },
  render: () => <AppChrome sidebar={false} data-id="element-3282" data-mp="1ou6yrhiol7z">
      <div className="px-7 py-6" data-id="element-3283" data-mp="1rzw26q5bn3p">
        <PulsesScreen data-id="element-3284" data-mp="3swgit7ee3tzx" />
      </div>
    </AppChrome>
}, {
  id: 'creator-skills',
  name: 'Skills library',
  note: 'One featured method and three compact library rows',
  use: 'Use it for the "your company\u2019s way of working, written down" beat. The featured skill explains the concept; the rows show that the method is owned, shared, and repeatedly reused.',
  // A narrow canvas keeps the list type legible in vertical video.
  width: 620,
  renderWidth: 560,
  centered: true,
  renderVertical: () => <SkillShelf spread={1} data-id="element-3285" data-mp="5txywfmorl77" />,
  render: () => <SkillShelf spread={2} data-id="element-3286" data-mp="5u7tyhml8ijr" />
},
/*
 * ── TRACK STORY ───────────────────────────────────────────────────────────
 * Six cuts from one lead-to-pipeline story. Each plate focuses on the content
 * that remains legible in a 9:16 crop while retaining the shared facts.
 */
{
  id: 'creator-track-chat',
  name: 'Track chat',
  note: 'Ask what changed; get risks and next actions',
  use: 'Open the Track sequence here. The question is relatable and the answer proves Giga checked CRM, email, rules and inbound activity rather than returning a generic summary.',
  width: 1040,
  renderWidth: 510,
  crop: {
    zoom: 0.76,
    x: 165
  },
  render: () => <TrackWorkspace trackName="Inbound leads → qualified pipeline" staticSection="chat" exportMode data-id="element-3287" data-mp="2e7bd4xxkwaxt" />
}, {
  id: 'creator-track-overview',
  name: 'Track overview',
  note: '18 enriched leads, four exceptions, $184k at risk',
  use: 'Follow Chat with the persistent state: the team sees the current pipeline and the source conversations without asking the same question again.',
  width: 1040,
  renderWidth: 510,
  crop: {
    zoom: 0.68,
    x: 22
  },
  render: () => <TrackWorkspace trackName="Inbound leads → qualified pipeline" staticSection="overview" exportMode data-id="element-3288" data-mp="1qd0etmdilf0r" />
}, {
  id: 'creator-track-linked',
  name: 'Track resources',
  note: 'Every tool and rule behind the result',
  use: 'Use this for scoped context: Close, Gmail, Clearbit, Slack, routing rules and the revenue playbook are visibly attached to this body of work.',
  width: 1040,
  renderWidth: 510,
  crop: {
    zoom: 0.7,
    x: 380
  },
  render: () => <TrackWorkspace trackName="Inbound leads → qualified pipeline" staticSection="linked" exportMode data-id="element-3289" data-mp="192egb9alh49d" />
}, {
  id: 'creator-track-activity',
  name: 'Track activity',
  note: 'Automated actions beside human decisions',
  use: 'Use this for auditability: lead routing, risk detection and source updates appear alongside the people who approved territory and qualification changes.',
  width: 1040,
  renderWidth: 510,
  crop: {
    zoom: 0.72,
    x: 180
  },
  render: () => <TrackWorkspace trackName="Inbound leads → qualified pipeline" staticSection="activity" exportMode data-id="element-3290" data-mp="1spu6evo4j8x2" />
}, {
  id: 'creator-track-pulses',
  name: 'Track pulses',
  note: 'The pipeline monitored without another prompt',
  use: 'Use this for always-on work: routing exceptions, Monday briefs and stalled deals are checked on a schedule and delivered to the right channel.',
  width: 1040,
  renderWidth: 510,
  crop: {
    zoom: 0.78,
    x: 155
  },
  render: () => <TrackWorkspace trackName="Inbound leads → qualified pipeline" staticSection="pulses" exportMode data-id="element-3291" data-mp="cbvrs3octrfe" />
}, {
  id: 'creator-track-drive',
  name: 'Track files',
  note: 'Generated reports stay with their source context',
  use: 'Close on durable output: a generated pipeline brief and routing report sit beside the approved field map and operations playbook.',
  width: 1040,
  renderWidth: 510,
  crop: {
    zoom: 0.78,
    x: 155
  },
  render: () => <TrackWorkspace trackName="Inbound leads → qualified pipeline" staticSection="drive" exportMode data-id="element-3292" data-mp="3mafh3kwxkebx" />
},
/*
 * ── THE AGENT SEQUENCE ────────────────────────────────────────────────────
 * Pulled from the AI Agents page, where they were built as idealised product
 * objects rather than screenshots. That is exactly why they belong here:
 * unlike a full app screen, each one is a SINGLE object about a SINGLE moment,
 * so it survives being watched on a phone without any cropping — and it gives
 * someone explaining the product a frame per step rather than one busy screen.
 *
 * Ordered as the sequence itself: describe the job, grant access, approve the
 * first action.
 */
/*
 * AN AGENT TEMPLATES plate opened this sequence. Dropped: it is the same
 * composition as the skills wall — a cropped grid of cards on a ruled board —
 * and two of those in one short pack read as a repeat rather than as two
 * ideas. The builder is the stronger opening anyway; it shows the job being
 * described rather than a menu of jobs.
 */
{
  id: 'creator-agent-builder',
  name: 'Agent builder',
  note: 'The job, described in plain words',
  use: 'The clearest single frame for "how do you make one?" — a brief in ordinary language, not a flowchart. Already phone-shaped, so it needs no cropping on a vertical cut.',
  // The brief sheet plus the settings panel it overlaps on the right, which is
  // absolutely positioned inside the card's own right padding.
  width: 380,
  object: true,
  render: () => <AgentBuilderCard data-id="element-3293" data-mp="15hplcskd1f4v" />
}, {
  id: 'creator-agent-connections',
  name: 'Agent access',
  note: 'Two granted, three waiting',
  use: 'The moment an agent is given the tools it needs. Small and single-purpose — good as an inset over footage of someone talking, rather than a full frame.',
  width: 230,
  object: true,
  render: () => <ConnectionsCard data-id="element-3294" data-mp="3h6cn02pv4z25" />
}, {
  id: 'creator-agent-approval',
  name: 'Approval prompt',
  note: 'Decline · approve · always allow',
  use: 'The most useful frame in the pack for an objection. Whenever someone asks what stops an agent doing something unattended, this is the answer — it shows the agent stopping to ask.',
  width: 300,
  object: true,
  render: () => <ApprovalCard data-id="element-3295" data-mp="2yejqvzfhyxq7" />
}];

/*
 * A SHARED AGENTS plate used to close this sequence. Dropped: the skills
 * library already makes the same point — one person writes something down and
 * the company inherits it — and it makes it with the author and the reuse
 * count visible, which the shared list did not. Two plates arguing one point
 * is one plate too many in a pack this short.
 */